// modals/FamilyStatementModal.js
// -----------------------------------------------------------------------------
// Parent-friendly family balance statement across all balance modules
// (afterschool, bus, activities, daycare, art school). Colonie is excluded
// because it isn't in BALANCE_MODULES. Replaces the old FamilyBalanceOverview +
// FamilyBalanceStatement pair.
//
// Design goals: lead with the amount due, plain-language rows (Item / Charged /
// Paid / Balance), settled items muted, outstanding ones highlighted, and a
// print stylesheet that keeps the key colors and stays on the page.
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';
import {
  BALANCE_MODULES,
  fetchModuleEnrollments,
  sumRows,
  emptyTotals,
  formatMoney as fmt,
  isLBP,
} from '../balanceModules';

const EPS = 0.005;

// FamilyYearlyBalances.Module → friendly label + currency. Mirrors the backend
// BALANCE_MODULE_MAP so prior-year arrears can be broken down per module.
const LEDGER_MODULES = {
  bus:          { label: 'Bus',          currency: 'USD' },
  activities:   { label: 'Activities',   currency: 'USD' },
  afterschool:  { label: 'CAP',           currency: 'USD' },
  artschool:    { label: 'Art School',   currency: 'USD' },
  daycare:      { label: 'Daycare',      currency: 'USD' },
  ifada:        { label: 'Ifada',        currency: 'LBP' },
  registration: { label: 'Registration', currency: 'LBP' },
};
const ledgerModuleInfo = (m) =>
  LEDGER_MODULES[String(m || '').toLowerCase()] || { label: m || 'Other', currency: 'USD' };

const FamilyStatementModal = ({ apiCall, family, academicYear, onClose, showError }) => {
  const [sections, setSections] = useState([]); // [{ module, rows, totals }]
  const [payments, setPayments] = useState([]);
  const [priorYears, setPriorYears] = useState([]); // [{ year, balance }] older years still owed
  const [schoolChildren, setSchoolChildren] = useState({ current: [], historical: [] });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('detailed'); // 'detailed' | 'summary'
  const [lbpRate, setLbpRate] = useState(89500);

  const familyId = family?.FamilyID;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch every owed module in parallel. Colonie is intentionally not here.
      const results = await Promise.all(
        BALANCE_MODULES.map(async (module) => {
          let rows;
          if (module.loadStatementRows) {
            // Charge-based modules (daycare) supply their own rows.
            rows = await module.loadStatementRows(apiCall, { familyId, academicYear });
          } else {
            // Scope to the statement's year so current-year charges don't mix
            // with prior years — prior-year arrears show as their own line below.
            const raw = await fetchModuleEnrollments(apiCall, module, { familyId, academicYear });
            rows = raw
              .filter((e) => e.Status === 'Active')
              .map(module.normalize)
              // Interim guard: drop phantom rows with no student identity.
              .filter((r) => r.studentName && r.studentName.trim());
          }
          return { module, rows, totals: sumRows(rows) };
        })
      );

      // Payments follow the statement's academic year: in 26-27 we only list
      // this year's receipts, not 25-26's. Daycare is the exception — it runs an
      // all-years rolling model, so its payments always show regardless of year.
      let pmts = [];
      try {
        const p = await apiCall(`/payments?familyId=${familyId}`);
        pmts = (Array.isArray(p) ? p : []).filter(
          (row) =>
            String(row.Module || '').toLowerCase() === 'daycare' ||
            String(row.AcademicYear) === String(academicYear)
        );
      } catch (err) {
        pmts = [];
      }

      // Modules that hold their payments outside our Payments table (SIS)
      // contribute them here, so they appear in the same "Payments Received"
      // list. A failing/unreachable source just adds nothing.
      const externalPayments = await Promise.all(
        BALANCE_MODULES.filter((m) => typeof m.loadPayments === 'function').map((m) =>
          m.loadPayments(apiCall, { familyId, academicYear }).catch(() => [])
        )
      );
      externalPayments.forEach((list) => {
        if (Array.isArray(list)) pmts = pmts.concat(list);
      });

      // Prior-year arrears from the central ledger (our modules only — SIS
      // tuition carries its own prior-year line in its section). Any academic
      // year older than this statement's year that still has a positive balance
      // shows as a distinct "<year> Balance" line, so 25-26 debt carries into
      // the 26-27 statement without being mixed into the current charges.
      let prior = [];
      try {
        const ledger = await apiCall(`/families/${familyId}/balances`);
        const byYear = {};
        (Array.isArray(ledger) ? ledger : []).forEach((r) => {
          const key = String(r.Module || '').toLowerCase();
          // Daycare is an all-years ledger shown in full in its own section, so
          // its prior-year balance would double-count here — skip it.
          if (key === 'daycare') return;
          if (r.AcademicYear && r.AcademicYear < academicYear && Number(r.Balance) > 0) {
            const mods = (byYear[r.AcademicYear] = byYear[r.AcademicYear] || {});
            mods[key] = (mods[key] || 0) + Number(r.Balance);
          }
        });
        prior = Object.entries(byYear)
          .map(([year, mods]) => {
            const modules = Object.entries(mods)
              .map(([module, balance]) => {
                const info = ledgerModuleInfo(module);
                return { module, label: info.label, currency: info.currency, balance };
              })
              .sort((a, b) => a.label.localeCompare(b.label));
            const totalUSD = modules
              .filter((m) => m.currency !== 'LBP')
              .reduce((s, m) => s + m.balance, 0);
            const totalLBP = modules
              .filter((m) => m.currency === 'LBP')
              .reduce((s, m) => s + m.balance, 0);
            return { year, modules, totalUSD, totalLBP };
          })
          .sort((a, b) => b.year.localeCompare(a.year));
      } catch (err) { /* ledger unavailable — omit prior-year lines */ }

      // School children (current from registration + historical who left) to
      // list as chips under the family name. Never fails the statement.
      let sc = { current: [], historical: [] };
      try {
        const r = await apiCall(
          `/families/${familyId}/school-children?academicYear=${encodeURIComponent(academicYear)}`
        );
        if (r && typeof r === 'object') {
          sc = { current: Array.isArray(r.current) ? r.current : [],
                 historical: Array.isArray(r.historical) ? r.historical : [] };
        }
      } catch (err) { /* no school-children source — omit */ }

      // LBP-to-USD exchange rate for the Grand Total conversion.
      try {
        const settings = await apiCall('/system/settings');
        const rateSetting = (Array.isArray(settings) ? settings : [])
          .find((s) => s.SettingKey === 'USDToLBPRate');
        const r = parseFloat(rateSetting?.SettingValue);
        if (r > 0) setLbpRate(r);
      } catch (_) { /* keep default */ }

      setSections(results.filter((s) => s.rows.length > 0));
      setPayments(pmts);
      setPriorYears(prior);
      setSchoolChildren(sc);
    } catch (err) {
      showError && showError('Error loading statement: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, familyId, academicYear, showError]);

  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape, lock background scroll while open.
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Grand totals across all sections.
  const grand = sections.reduce(
    (t, s) => ({
      cost: t.cost + s.totals.cost,
      discount: t.discount + s.totals.discount,
      final: t.final + s.totals.final,
      paid: t.paid + s.totals.paid,
      balance: t.balance + s.totals.balance,
    }),
    emptyTotals()
  );

  // Per-module LBP totals. Our own charge modules are USD, so these come from
  // SIS tuition: its LBP rows carry finalLBP / paidLBP / balanceLBP and surface
  // here automatically alongside the USD grand totals.
  const lbpTotals = (rows) =>
    rows.reduce(
      (t, r) => ({
        final: t.final + (r.finalLBP || 0),
        paid: t.paid + (r.paidLBP || 0),
        balance: t.balance + (r.balanceLBP || 0),
      }),
      { final: 0, paid: 0, balance: 0 }
    );
  const grandLBP = sections.reduce(
    (t, s) => {
      const l = lbpTotals(s.rows);
      return { final: t.final + l.final, paid: t.paid + l.paid, balance: t.balance + l.balance };
    },
    { final: 0, paid: 0, balance: 0 }
  );
  // Prior-year arrears (25-26 etc.) carry into what's owed now, per currency.
  const priorTotalUSD = priorYears.reduce((s, p) => s + (p.totalUSD || 0), 0);
  const priorTotalLBP = priorYears.reduce((s, p) => s + (p.totalLBP || 0), 0);
  const dueLBP = grandLBP.balance + priorTotalLBP;
  const hasLBPCharges = Math.abs(grandLBP.final) > EPS || Math.abs(dueLBP) > EPS;

  // Payments may be USD or LBP (ifada). Keep them separate — never merge.
  const paymentsUSD = payments.filter((p) => !isLBP(p.Currency)).reduce((s, p) => s + (p.Amount || 0), 0);
  const paymentsLBP = payments.filter((p) => isLBP(p.Currency)).reduce((s, p) => s + (p.Amount || 0), 0);
  const hasUSDPayments = payments.some((p) => !isLBP(p.Currency));
  const hasLBPPayments = payments.some((p) => isLBP(p.Currency));

  // Distinct children across all services + school registration + history.
  // Current children (a service row, or a registration entry) carry their class
  // or program; students who left carry the last academic year they attended
  // (leftYear). Pseudo-rows like "Old Balance" are skipped.
  const children = (() => {
    const map = new Map();
    const ensure = (name) => {
      const key = name.toLowerCase();
      if (!map.has(key)) map.set(key, { name, grade: null, programs: new Set(), leftYear: null, isCurrent: false, fromDaycare: false });
      return map.get(key);
    };
    // 1) Children with active service rows (daycare, afterschool, …) — current.
    sections.forEach(({ module, rows }) => {
      rows.forEach((r) => {
        const name = (r.studentName || '').trim();
        if (!name || /^(old balance|daycare)$/i.test(name)) return;
        const c = ensure(name);
        c.isCurrent = true;
        c.programs.add(module.label);
        const g = (r.grade || '').trim();
        if (g && g !== '-' && !c.grade) c.grade = g;
      });
    });
    // 2) Current school children from the family's registration for this year.
    (schoolChildren.current || []).forEach((s) => {
      const name = (s.name || '').trim();
      if (!name) return;
      const c = ensure(name);
      c.isCurrent = true;
      if (s.class && !c.grade) c.grade = s.class;
      if (s.fromDaycare) c.fromDaycare = true;
    });
    // 3) Students who left — tag with the last year attended (never a current one).
    (schoolChildren.historical || []).forEach((s) => {
      const name = (s.name || '').trim();
      if (!name) return;
      const c = ensure(name);
      if (s.class && !c.grade) c.grade = s.class;
      if (s.leftYear && !c.leftYear && !c.isCurrent) c.leftYear = s.leftYear;
      if (s.fromDaycare) c.fromDaycare = true;
    });
    return Array.from(map.values()).map((c) => ({
      name: c.name,
      label: c.grade || Array.from(c.programs).join(', '),
      leftYear: c.isCurrent ? null : c.leftYear,
      fromDaycare: c.fromDaycare,
    }));
  })();

  const familyName =
    (family?.FatherName || '') + (family?.MotherName ? ` & ${family.MotherName}` : '');

  const due = grand.balance + priorTotalUSD;
  const grandTotalUSD = due + (dueLBP / lbpRate);
  const hero =
    due > EPS
      ? { word: 'Balance Due', box: 'bg-rose-50 border-rose-200', amt: 'text-rose-700' }
      : due < -EPS
      ? { word: 'Credit Balance', box: 'bg-emerald-50 border-emerald-200', amt: 'text-emerald-700' }
      : { word: 'Paid in Full', box: 'bg-emerald-50 border-emerald-200', amt: 'text-emerald-700' };
  const heroAmount = fmt(Math.abs(due));

  const content = (
    <div
      className="statement-portal statement-backdrop fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <style>{`
        @media print {
          /* Only the portaled statement prints. Hiding the rest of the app with
             display:none (not visibility:hidden) means it contributes ZERO
             height — no blank trailing pages, no leftover white space. */
          body > *:not(.statement-portal) { display: none !important; }
          .statement-portal {
            position: static !important; inset: auto !important;
            background: none !important; padding: 0 !important; margin: 0 !important;
            overflow: visible !important; display: block !important; height: auto !important;
          }
          .statement-card {
            position: static !important; max-height: none !important; overflow: visible !important;
            box-shadow: none !important; border-radius: 0 !important; margin: 0 !important;
            max-width: none !important; width: 100% !important;
          }
          #family-statement-print {
            width: 100% !important; padding: 0 !important;
            -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
          }
          #family-statement-print table { table-layout: fixed !important; width: 100% !important; }
          #family-statement-print th, #family-statement-print td { overflow-wrap: anywhere; word-break: break-word; }
          /* Keep small blocks whole; let long module cards flow across pages
             (forcing them whole is what left big white gaps). Never split a row. */
          .stmt-keep { break-inside: avoid; page-break-inside: avoid; }
          #family-statement-print tr { break-inside: avoid; page-break-inside: avoid; }
          .stmt-footer { break-before: avoid; }
          .no-print { display: none !important; }
        }
        @page { size: A4; margin: 1.2cm; }
      `}</style>

      <div
        className="statement-card bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar (hidden in print) */}
        <div className="no-print sticky top-0 z-10 flex justify-between items-center px-6 py-3 border-b border-slate-200 bg-white/95 backdrop-blur rounded-t-xl">
          <div>
            <h2 className="text-base font-semibold text-slate-800">{familyName || 'Family Statement'}</h2>
            <p className="text-xs text-slate-400">Family ID: {familyId}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="no-print flex rounded-lg border border-slate-200 p-0.5 text-sm">
              <button
                onClick={() => setView('detailed')}
                className={`px-3 py-1.5 rounded-md ${view === 'detailed' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                Detailed
              </button>
              <button
                onClick={() => setView('summary')}
                className={`px-3 py-1.5 rounded-md ${view === 'summary' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                Summary
              </button>
            </div>
            <button
              onClick={() => window.print()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              <Printer size={16} /> Print
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm"
            >
              <X size={16} /> Close
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading statement…</div>
        ) : (
          <div
            id="family-statement-print"
            className="p-7 text-slate-800"
            style={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '12px', lineHeight: 1.45 }}
          >
            {/* Header */}
            <div className="flex justify-between items-start pb-4 border-b border-slate-200">
              <div>
                <h1 className="text-lg font-bold text-slate-900 leading-tight">Collège des Apôtres</h1>
                <p className="text-slate-500">Family Balance Statement</p>
                <p className="text-xs text-slate-400 mt-0.5">Academic Year {academicYear}</p>
              </div>
              <div className="text-right text-xs text-slate-500 leading-relaxed">
                <p>Family ID: <span className="font-medium text-slate-700">{familyId}</span></p>
                {family?.PrimaryPhone && <p>{family.PrimaryPhone}</p>}
                <p>{new Date().toLocaleDateString()}</p>
              </div>
            </div>

            {/* Prepared for + Amount due hero */}
            <div className="flex flex-col sm:flex-row gap-3 mt-4 mb-5">
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Prepared for</p>
                <p className="text-base font-semibold text-slate-900">{family?.FatherName}</p>
                {family?.MotherName && <p className="text-sm text-slate-600">{family.MotherName}</p>}
                {children.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {children.map((c) => (
                      <span
                        key={c.name}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] ${
                          c.leftYear ? 'bg-slate-50 text-slate-400' : 'bg-slate-100'
                        }`}
                      >
                        <span className={c.leftYear ? 'font-medium text-slate-500' : 'font-medium text-slate-700'}>{c.name}</span>
                        {c.label && (
                          <>
                            <span className="text-slate-400">·</span>
                            <span className="text-slate-500">{c.label}</span>
                          </>
                        )}
                        {c.leftYear && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span className="text-slate-400">left {c.leftYear}</span>
                          </>
                        )}
                        {c.fromDaycare && (
                          <span
                            className="text-indigo-600"
                            title="This student came from daycare"
                          >
                            🏫 from daycare
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className={`stmt-keep flex items-center justify-between gap-6 px-5 py-3 rounded-xl border ${hero.box} sm:min-w-[260px]`}>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{hero.word}</p>
                  <p className={`text-2xl font-bold leading-tight ${hero.amt}`}>{heroAmount}</p>
                  {hasLBPCharges && (
                    <p className={`text-base font-bold leading-tight ${hero.amt}`}>
                      {fmt(Math.abs(dueLBP), 'LBP')}
                    </p>
                  )}
                  {hasLBPCharges && (
                    <p className="mt-1 pt-1 border-t border-slate-200/60 text-[11px] uppercase tracking-wide text-slate-500">
                      Grand Total <span className={`text-base font-bold ${hero.amt}`}>{fmt(Math.abs(grandTotalUSD))}</span>
                    </p>
                  )}
                </div>
                <div className="text-right text-[11px] text-slate-500 leading-relaxed">
                  <p>Charged <span className="font-medium text-slate-700">{fmt(grand.final)}</span></p>
                  {priorTotalUSD > EPS && (
                    <p>Previous <span className="font-medium text-slate-700">{fmt(priorTotalUSD)}</span></p>
                  )}
                  <p>Paid <span className="font-medium text-slate-700">{fmt(grand.paid)}</span></p>
                  {hasLBPCharges && (
                    <p className="mt-1 pt-1 border-t border-slate-200/70">{fmt(dueLBP, 'LBP')} due</p>
                  )}
                  {hasLBPCharges && (
                    <p className="text-[10px] text-slate-400">@ {lbpRate.toLocaleString()} L.L./$</p>
                  )}
                </div>
              </div>
            </div>

            {sections.length === 0 && priorYears.length === 0 ? (
              <p className="text-center text-slate-400 py-10">No charges found for this family.</p>
            ) : view === 'summary' ? (
              <>
                {/* Summary: one row per service (only when there are current-year
                    services; a prior-years-only family shows just the card below). */}
                {sections.length > 0 && (
                <div className="stmt-keep mt-1 rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                    <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Summary by Service</span>
                  </div>
                  <table className="w-full">
                    <colgroup>
                      <col style={{ width: '46%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                    </colgroup>
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="text-left font-medium px-4 py-1.5">Service</th>
                        <th className="text-right font-medium px-2 py-1.5">Charged</th>
                        <th className="text-right font-medium px-2 py-1.5">Paid</th>
                        <th className="text-right font-medium px-4 py-1.5">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sections.map(({ module, totals, rows }) => {
                        const l = lbpTotals(rows);
                        const owed = totals.balance;
                        return (
                          <tr key={module.key} className="border-b border-slate-50">
                            <td className="px-4 py-2 font-medium text-slate-800">{module.label}</td>
                            <td className="px-2 py-2 text-right text-slate-600">
                              {fmt(totals.final)}
                              {l.final > EPS && <div className="text-[11px] text-slate-400">{fmt(l.final, 'LBP')}</div>}
                            </td>
                            <td className="px-2 py-2 text-right text-slate-600">
                              {fmt(totals.paid)}
                              {l.paid > EPS && <div className="text-[11px] text-slate-400">{fmt(l.paid, 'LBP')}</div>}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className={owed > EPS ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-medium'}>
                                {owed > EPS ? fmt(owed) : 'Paid'}
                              </span>
                              {Math.abs(l.balance) > EPS && (
                                <div className="text-[11px] text-rose-600 font-medium">{fmt(l.balance, 'LBP')}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-slate-50/60">
                        <td className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Total</td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmt(grand.final)}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmt(grand.paid)}</td>
                        <td className="px-4 py-2 text-right font-semibold">
                          <span className={grand.balance > EPS ? 'text-rose-600' : 'text-emerald-600'}>{fmt(grand.balance)}</span>
                          {hasLBPCharges && <div className="text-[11px] text-rose-600">{fmt(grandLBP.balance, 'LBP')}</div>}
                          {hasLBPCharges && <div className="text-[10px] text-slate-500 mt-0.5 pt-0.5 border-t border-slate-200">Grand Total {fmt(Math.abs(grandTotalUSD))}</div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                )}

                {/* Previous-year arrears — shown in summary too, so the amount
                    due at the top reconciles with what's listed. */}
                {priorYears.length > 0 && <PriorYearsCard priorYears={priorYears} />}

                {/* Payments received — totals only */}
                {(hasUSDPayments || hasLBPPayments) && (
                  <div className="stmt-keep mt-4 rounded-xl border border-slate-200 px-4 py-3">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 mb-1">Payments Received</p>
                    <div className="flex flex-col gap-0.5 text-sm">
                      {hasUSDPayments && (
                        <div className="flex justify-between"><span className="text-slate-500">Total (USD)</span><span className="font-medium text-slate-800">{fmt(paymentsUSD, 'USD')}</span></div>
                      )}
                      {hasLBPPayments && (
                        <div className="flex justify-between"><span className="text-slate-500">Total (LBP)</span><span className="font-medium text-slate-800">{fmt(paymentsLBP, 'LBP')}</span></div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Detailed: one card per owed module */}
                {sections.map(({ module, rows, totals }) => (
                  <ModuleSection key={module.key} module={module} rows={rows} totals={totals} />
                ))}

                {/* Previous-year arrears carried into this year (our modules; SIS
                    tuition carries its own prior-year line in its section). */}
                {priorYears.length > 0 && <PriorYearsCard priorYears={priorYears} />}

                {/* Payments received — full history */}
                {payments.length > 0 && (
              <div className="mt-5 rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Payments Received</span>
                </div>
                <table className="w-full">
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '36%' }} />
                    <col style={{ width: '24%' }} />
                  </colgroup>
                  <tbody>
                    {payments.map((p, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="px-4 py-1.5 text-slate-500">{new Date(p.PaymentDate).toLocaleDateString()}</td>
                        <td className="px-2 py-1.5 text-slate-700">{p.Module || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-400 text-[11px]">
                          {p.PaymentMethod}
                          {p.ReferenceNumber ? ` · ${p.ReferenceNumber}` : ''}
                        </td>
                        <td className="px-4 py-1.5 text-right font-medium text-slate-700">{fmt(p.Amount, p.Currency)}</td>
                      </tr>
                    ))}
                    {hasUSDPayments && (
                      <tr className="bg-slate-50">
                        <td colSpan="3" className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500">Total received (USD)</td>
                        <td className="px-4 py-1.5 text-right font-semibold text-slate-800">{fmt(paymentsUSD, 'USD')}</td>
                      </tr>
                    )}
                    {hasLBPPayments && (
                      <tr className="bg-slate-50 border-t border-slate-100">
                        <td colSpan="3" className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500">Total received (LBP)</td>
                        <td className="px-4 py-1.5 text-right font-semibold text-slate-800">{fmt(paymentsLBP, 'LBP')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
              </>
            )}

            {/* Bottom totals strip (detailed view only) */}
            {view === 'detailed' && (sections.length > 0 || priorYears.length > 0) && (
              <div className="stmt-keep flex justify-end mt-5">
                <div className="w-full sm:w-72 text-sm">
                  <div className="flex justify-between py-1 text-slate-500">
                    <span>Total charged</span><span className="text-slate-700">{fmt(grand.final)}</span>
                  </div>
                  {priorTotalUSD > EPS && (
                    <div className="flex justify-between py-1 text-slate-500">
                      <span>Previous balance</span><span className="text-slate-700">{fmt(priorTotalUSD)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 text-slate-500 border-b border-slate-200">
                    <span>Total paid</span><span className="text-slate-700">{fmt(grand.paid)}</span>
                  </div>
                  <div className="flex justify-between pt-2">
                    <span className="font-semibold text-slate-800">{hero.word}</span>
                    <span className={`font-bold text-base ${hero.amt}`}>{heroAmount}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="stmt-footer mt-6 pt-3 border-t border-slate-200 text-[11px] text-center text-slate-400">
              <p>For any questions about this statement, please contact the school office.</p>
              <p>Thank you for your continued trust in Collège des Apôtres.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render outside the app tree so printing can hide everything else and the
  // family list behind the modal adds no height (no blank trailing pages).
  return typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
};

// Previous-year arrears from the central ledger, broken down per module under
// each year. USD and LBP modules are formatted in their own currency; the year
// header sums each currency separately (never mixing the two).
const PriorYearsCard = ({ priorYears }) => (
  <div className="stmt-keep mt-5 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
    <div className="px-4 py-2 bg-amber-100 border-b border-amber-200">
      <span className="text-[11px] uppercase tracking-wide font-semibold text-amber-800">Previous Years — Carried Over</span>
    </div>
    <table className="w-full text-sm">
      <tbody>
        {priorYears.map((p) => (
          <React.Fragment key={p.year}>
            <tr className="bg-amber-100/60 border-b border-amber-200">
              <td className="px-4 py-1.5 text-[11px] uppercase tracking-wide font-semibold text-amber-800">{p.year}</td>
              <td className="px-4 py-1.5 text-right text-[11px] font-semibold text-amber-800">
                {[
                  p.totalUSD > EPS ? fmt(p.totalUSD) : null,
                  p.totalLBP > EPS ? fmt(p.totalLBP, 'LBP') : null,
                ].filter(Boolean).join(' + ')}
              </td>
            </tr>
            {p.modules.map((m) => (
              <tr key={`${p.year}-${m.module}`} className="border-b border-amber-100 last:border-0">
                <td className="pl-8 pr-4 py-1.5 text-amber-900">{m.label}</td>
                <td className="px-4 py-1.5 text-right font-medium text-amber-900">{fmt(m.balance, m.currency)}</td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

// One module card: Item / Charged / Paid / Balance. Settled rows are muted with
// a "Paid" tag; outstanding rows show the balance in red.
const ModuleSection = ({ module, rows, totals }) => {
  const owed = totals.balance;
  const owedLBP = rows.reduce((s, r) => s + (r.balanceLBP || 0), 0);
  return (
    <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex justify-between items-center px-4 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">{module.label}</span>
        {owed > EPS || owedLBP > EPS ? (
          <span className="text-xs font-semibold text-rose-600">
            Owed {[owed > EPS ? fmt(owed) : null, owedLBP > EPS ? fmt(owedLBP, 'LBP') : null]
              .filter(Boolean).join(' + ')}
          </span>
        ) : (
          <span className="text-xs font-medium text-emerald-600">Settled</span>
        )}
      </div>
      <table className="w-full">
        <colgroup>
          <col style={{ width: '50%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '18%' }} />
        </colgroup>
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="text-left font-medium px-4 py-1.5">Item</th>
            <th className="text-right font-medium px-2 py-1.5">Charged</th>
            <th className="text-right font-medium px-2 py-1.5">Paid</th>
            <th className="text-right font-medium px-4 py-1.5">Balance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            // A row is either USD or LBP. LBP amounts live in the *LBP mirror
            // fields; read whichever side this row carries so a Lebanese-pound
            // charge doesn't render as $0.00.
            const isLbpRow = (r.currency || '').toUpperCase() === 'LBP'
              || (r.finalLBP || r.paidLBP || r.balanceLBP);
            const cur = isLbpRow ? 'LBP' : 'USD';
            const rFinal = isLbpRow ? (r.finalLBP || 0) : (r.final || 0);
            const rPaid = isLbpRow ? (r.paidLBP || 0) : (r.paid || 0);
            const rBalance = isLbpRow ? (r.balanceLBP || 0) : (r.balance || 0);
            const rDiscount = isLbpRow ? (r.discountLBP || 0) : (r.discount || 0);
            const settled = Math.abs(rBalance) < EPS;
            const credit = rBalance < -EPS;
            const sub = [r.studentName, billingLabel(r.billing)].filter(Boolean).join(' · ');
            return (
              <tr key={idx} className="border-b border-slate-50 align-top">
                <td className="px-4 py-1.5">
                  <div className={settled ? 'text-slate-400' : 'font-medium text-slate-800'}>{r.description}</div>
                  {(sub || r.note) && (
                    <div className="text-[11px] text-slate-400">
                      {sub}{r.note ? ` · ${r.note}` : ''}
                    </div>
                  )}
                </td>
                <td className={`px-2 py-1.5 text-right ${settled ? 'text-slate-400' : 'text-slate-700'}`}>
                  {fmt(rFinal, cur)}
                  {rDiscount > EPS && (
                    <div className="text-[10px] text-emerald-600">−{fmt(rDiscount, cur)} off</div>
                  )}
                </td>
                <td className={`px-2 py-1.5 text-right ${settled ? 'text-slate-400' : 'text-slate-700'}`}>{fmt(rPaid, cur)}</td>
                <td className="px-4 py-1.5 text-right">
                  {credit ? (
                    <span className="text-emerald-600 font-medium">Credit {fmt(Math.abs(rBalance), cur)}</span>
                  ) : settled ? (
                    <span className="text-emerald-600 font-medium">Paid</span>
                  ) : (
                    <span className="text-rose-600 font-semibold">{fmt(rBalance, cur)}</span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length > 1 && (
            <>
              {/* USD subtotal — only when the section has USD charges. */}
              {(totals.final > EPS || totals.paid > EPS || Math.abs(owed) > EPS) && (
                <tr className="bg-slate-50/60">
                  <td className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500">Subtotal</td>
                  <td className="px-2 py-1.5 text-right text-slate-600">{fmt(totals.final)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-600">{fmt(totals.paid)}</td>
                  <td className="px-4 py-1.5 text-right font-semibold">
                    <span className={owed > EPS ? 'text-rose-600' : 'text-emerald-600'}>{fmt(owed)}</span>
                  </td>
                </tr>
              )}
              {/* LBP subtotal — only when the section has LBP charges. */}
              {(() => {
                const lFinal = rows.reduce((s, r) => s + (r.finalLBP || 0), 0);
                const lPaid = rows.reduce((s, r) => s + (r.paidLBP || 0), 0);
                const lBal = rows.reduce((s, r) => s + (r.balanceLBP || 0), 0);
                if (lFinal < EPS && lPaid < EPS && Math.abs(lBal) < EPS) return null;
                return (
                  <tr className="bg-slate-50/60">
                    <td className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500">Subtotal (LBP)</td>
                    <td className="px-2 py-1.5 text-right text-slate-600">{fmt(lFinal, 'LBP')}</td>
                    <td className="px-2 py-1.5 text-right text-slate-600">{fmt(lPaid, 'LBP')}</td>
                    <td className="px-4 py-1.5 text-right font-semibold">
                      <span className={lBal > EPS ? 'text-rose-600' : 'text-emerald-600'}>{fmt(lBal, 'LBP')}</span>
                    </td>
                  </tr>
                );
              })()}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

const billingLabel = (billingType) => {
  if (!billingType) return '';
  const t = String(billingType).toLowerCase();
  if (t === 'monthly') return 'Monthly';
  if (t.includes('trimester')) return billingType; // keeps "Trimester 1"
  if (t === 'annual' || t === 'yearly') return 'Annual';
  if (t === 'onetime' || t === 'one-time') return 'One-time';
  return billingType; // already friendly (Sport / Activity / Old Balance / Monthly Charge)
};

export default FamilyStatementModal;
